import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { motion } from "framer-motion";
import { FileText, AlertTriangle, Trash2, ArrowLeft, RefreshCw, FileSpreadsheet, DollarSign, Flag, FileSearch, CheckCircle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { toast } from "sonner";
import { getAuthHeaders } from "@/context/AuthContext";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = BACKEND_URL + "/api";

function DiscrepancyItem({ d, doc1, doc2 }) {
  const iconColor = d.severity === "high" ? "bg-red-500/20 text-red-400" : "bg-amber-500/20 text-amber-400";
  return (
    <div className="p-4 hover:bg-white/5">
      <div className="flex items-start gap-3">
        <div className={`w-8 h-8 rounded-lg flex items-center justify-center shrink-0 ${iconColor}`}>
          {d.category === "flagged_word" ? <Flag size={14} /> : <AlertTriangle size={14} />}
        </div>
        <div className="flex-1">
          <div className="flex items-center gap-2 mb-1">
            <span className="text-sm font-semibold text-white">{d.field}</span>
            <span className={`px-2 py-0.5 rounded text-xs uppercase ${d.severity === "high" ? "bg-red-500/20 text-red-400" : "bg-amber-500/20 text-amber-400"}`}>
              {d.severity}
            </span>
          </div>
          <p className="text-xs text-zinc-400 mb-2">{d.description}</p>
          <div className="grid grid-cols-2 gap-3">
            <div className="p-2 rounded bg-zinc-800/50 border border-white/5">
              <p className="text-xs text-zinc-500 mb-1">{doc1}</p>
              <p className="text-xs text-zinc-200 font-mono">{d.quotation_value}</p>
            </div>
            <div className="p-2 rounded bg-zinc-800/50 border border-white/5">
              <p className="text-xs text-zinc-500 mb-1">{doc2}</p>
              <p className="text-xs text-zinc-200 font-mono">{d.po_value}</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function DiscrepancyList({ discrepancies, doc1, doc2 }) {
  if (!discrepancies || discrepancies.length === 0) {
    return (
      <Card className="bg-zinc-900/50 border-white/5">
        <CardContent className="p-12 text-center">
          <CheckCircle className="mx-auto text-emerald-400 mb-4" size={48} />
          <p className="text-lg text-zinc-300">No findings</p>
          <p className="text-sm text-zinc-500">{doc1} and {doc2} match</p>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="bg-zinc-900/50 border-white/5">
      <CardContent className="p-0 divide-y divide-white/5">
        {discrepancies.map((d, idx) => (
          <DiscrepancyItem key={idx} d={d} doc1={doc1} doc2={doc2} />
        ))}
      </CardContent>
    </Card>
  );
}

export default function ResultPage() {
  const [comp, setComp] = useState(null);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState("vendor_ivalue");
  const navigate = useNavigate();
  const compId = window.location.pathname.split("/result/")[1];

  useEffect(() => {
    if (compId) {
      axios.get(`${API}/comparisons/${compId}`, { headers: getAuthHeaders() })
        .then((res) => setComp(res.data))
        .catch(() => { toast.error("Failed to load"); navigate("/"); })
        .finally(() => setLoading(false));
    }
  }, [compId, navigate]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <RefreshCw className="animate-spin text-blue-500" size={32} />
      </div>
    );
  }
  if (!comp) return null;

  const vendorCount = (comp.vendor_vs_ivalue_discrepancies || []).length;
  const poCount = (comp.ivalue_vs_po_discrepancies || []).length;

  const downloadReport = async (kind) => {
    try {
      const res = await axios.get(`${API}/comparisons/${compId}/report/${kind}`, {
        headers: getAuthHeaders(),
        responseType: "blob",
      });
      const ext = kind === "excel" ? "xlsx" : "pdf";
      const url = URL.createObjectURL(res.data);
      const link = document.createElement("a");
      link.href = url;
      link.download = `qpro_${comp.quotation_id}_v${comp.version}.${ext}`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      URL.revokeObjectURL(url);
    } catch {
      toast.error("Failed to download report");
    }
  };
  const downloadExcel = () => downloadReport("excel");
  const downloadPdf = () => downloadReport("pdf");

  return (
    <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="min-h-screen p-8">
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="icon" onClick={() => navigate("/")} className="text-zinc-400 hover:text-white">
            <ArrowLeft size={20} />
          </Button>
          <div>
            <h1 className="text-xl font-bold text-white">
              <span className="text-blue-400">{comp.quotation_id}</span>
              <span className="text-zinc-500 text-sm ml-2">v{comp.version}</span>
            </h1>
            <p className="text-zinc-500 text-sm">{comp.company_name}</p>
          </div>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={() => navigate(`/viewer/${compId}`)} className="border-zinc-700 text-zinc-300 hover:bg-zinc-800">
            <FileSearch size={16} className="mr-2" />View PDFs
          </Button>
          <Button variant="outline" onClick={downloadExcel} className="border-zinc-700 text-zinc-300 hover:bg-zinc-800">
            <FileSpreadsheet size={16} className="mr-2" />Excel
          </Button>
          <Button variant="outline" onClick={downloadPdf} className="border-zinc-700 text-zinc-300 hover:bg-zinc-800">
            <FileText size={16} className="mr-2" />PDF
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-3 gap-4 mb-6">
        <Card className="bg-zinc-900/50 border-white/5">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <span className="text-zinc-500 text-sm">Total Findings</span>
              <AlertTriangle className="text-amber-400" size={18} />
            </div>
            <p className="text-2xl font-bold text-white mt-1">{comp.total_discrepancies}</p>
          </CardContent>
        </Card>
        <Card className="border-orange-500/20 bg-gradient-to-r from-orange-500/10 to-blue-500/10">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <span className="text-sm">
                <span className="text-orange-400 font-medium">Vendor</span>
                <span className="text-zinc-500 mx-1">vs</span>
                <span className="text-blue-400 font-medium">iValue</span>
              </span>
              <DollarSign className={vendorCount > 0 ? "text-amber-400" : "text-emerald-400"} size={18} />
            </div>
            <p className="text-2xl font-bold text-white mt-1">{vendorCount}</p>
          </CardContent>
        </Card>
        <Card className="border-blue-500/20 bg-gradient-to-r from-blue-500/10 to-emerald-500/10">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <span className="text-sm">
                <span className="text-blue-400 font-medium">iValue</span>
                <span className="text-zinc-500 mx-1">vs</span>
                <span className="text-emerald-400 font-medium">Partner PO</span>
              </span>
              <Flag className={poCount > 0 ? "text-red-400" : "text-emerald-400"} size={18} />
            </div>
            <p className="text-2xl font-bold text-white mt-1">{poCount}</p>
          </CardContent>
        </Card>
      </div>

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList className="bg-zinc-900 border border-white/10">
          <TabsTrigger value="vendor_ivalue" className="data-[state=active]:bg-orange-500/20 data-[state=active]:text-orange-300">
            <span className="text-orange-400">Vendor</span><span className="text-zinc-500 mx-1">vs</span><span className="text-blue-400">iValue</span> ({vendorCount})
          </TabsTrigger>
          <TabsTrigger value="ivalue_po" className="data-[state=active]:bg-emerald-500/20 data-[state=active]:text-emerald-300">
            <span className="text-blue-400">iValue</span><span className="text-zinc-500 mx-1">vs</span><span className="text-emerald-400">Partner PO</span> ({poCount})
          </TabsTrigger>
        </TabsList>

        <TabsContent value="vendor_ivalue" className="mt-4">
          <DiscrepancyList discrepancies={comp.vendor_vs_ivalue_discrepancies || []} doc1="Vendor" doc2="iValue" />
        </TabsContent>

        <TabsContent value="ivalue_po" className="mt-4">
          <DiscrepancyList discrepancies={comp.ivalue_vs_po_discrepancies || []} doc1="iValue" doc2="Partner PO" />
        </TabsContent>
      </Tabs>
    </motion.div>
  );
}
